/**
 * themeContext.tsx
 *
 * Global React context that manages two cross-cutting concerns:
 *
 *  1. **Visual theme** — `"default"` (parchment), `"light"` (ivory), or `"dark"` (black arcane).
 *     The selected theme is persisted in a long-lived cookie (`tarot-theme`) so it survives
 *     page reloads and is available server-side for SSR.
 *
 *  2. **Active deck index** — which tarot deck artwork is currently displayed.
 *     Each `TarotCard.imageUrl` array holds one image per deck; `deckIndex` selects the
 *     active entry. The value is persisted in `localStorage` under `tarot-deck-index`.
 *
 * Usage:
 * ```tsx
 * // Wrap the app (already done in layout.tsx):
 * <ThemeProvider>{children}</ThemeProvider>
 *
 * // Consume anywhere in the tree:
 * const { theme, setTheme, deckIndex, setDeckIndex } = useTheme();
 * ```
 */

'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TAROT_CARDS } from '@/data/tarotData';

/** The three supported visual themes for the application. */
export type Theme = 'default' | 'light' | 'dark';

/** localStorage key used to persist the active deck index across sessions. */
const DECK_STORAGE_KEY = 'tarot-deck-index';

/**
 * Shape of the value provided by `ThemeContext`.
 *
 * @property theme        - Currently active visual theme.
 * @property setTheme     - Update the active theme; persists to cookie and applies CSS.
 * @property deckIndex    - Zero-based index of the currently selected deck artwork.
 * @property setDeckIndex - Update the deck index; clamps to valid range and persists to localStorage.
 */
interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  deckIndex: number;
  setDeckIndex: (index: number) => void;
}

/** Default context value used before the provider mounts (no-op setters). */
const ThemeContext = createContext<ThemeContextValue>({
  theme: 'default',
  setTheme: () => {},
  deckIndex: 0,
  setDeckIndex: () => {},
});

/**
 * Read a cookie value by name.
 *
 * @param name - The cookie name to look up.
 * @returns The decoded cookie value, or `null` if not found or running server-side.
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Write a cookie with a default 1-year expiry.
 *
 * @param name  - Cookie name.
 * @param value - Cookie value (will be URI-encoded).
 * @param days  - Expiry in days (default 365).
 */
function setCookie(name: string, value: string, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

/**
 * ThemeProvider
 *
 * Provides `ThemeContext` to the component tree. Must wrap the application root
 * (see `src/app/layout.tsx`).
 *
 * On mount it:
 * - Reads the saved theme from the `tarot-theme` cookie and applies it to `<html>`.
 * - Reads the saved deck index from `localStorage` and validates it against the
 *   number of available deck images.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('default');
  const [deckIndex, setDeckIndexState] = useState<number>(0);

  /** Total number of deck variants available (derived from the first card's imageUrl array). */
  const deckCount = TAROT_CARDS[0]?.imageUrl?.length ?? 0;

  /**
   * Clamp a deck index to the valid range `[0, deckCount)`.
   * Returns `0` for any out-of-range value.
   */
  const normalizeDeckIndex = useCallback((index: number) => {
    if (index < 0 || index >= deckCount) {
      return 0;
    }
    return index;
  }, [deckCount]);

  /** Restore persisted theme and deck index on first render. */
  useEffect(() => {
    const saved = getCookie('tarot-theme') as Theme | null;
    if (saved && ['default', 'light', 'dark'].includes(saved)) {
      setThemeState(saved);
      applyTheme(saved);
    }

    try {
      const storedDeck = localStorage.getItem(DECK_STORAGE_KEY);
      if (storedDeck !== null) {
        const parsed = parseInt(storedDeck, 10);
        if (!isNaN(parsed)) {
          setDeckIndexState(normalizeDeckIndex(parsed));
        }
      }
    } catch { /* ignore */ }
  }, [normalizeDeckIndex]);

  /**
   * Update the active theme, persist it to a cookie, and apply CSS changes.
   *
   * @param t - The new theme to activate.
   */
  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    setCookie('tarot-theme', t);
    applyTheme(t);
  }, []);

  /**
   * Update the active deck index, clamp it to the valid range, and persist to localStorage.
   *
   * @param index - Zero-based deck index to activate.
   */
  const setDeckIndex = useCallback((index: number) => {
    const validIndex = normalizeDeckIndex(index);
    setDeckIndexState(validIndex);
    try {
      localStorage.setItem(DECK_STORAGE_KEY, String(validIndex));
    } catch { /* ignore */ }
  }, [normalizeDeckIndex]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, deckIndex, setDeckIndex }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme
 *
 * Convenience hook that returns the current `ThemeContextValue`.
 * Must be called inside a component that is a descendant of `ThemeProvider`.
 *
 * @returns `{ theme, setTheme, deckIndex, setDeckIndex }`
 */
export function useTheme() {
  return useContext(ThemeContext);
}

/**
 * Apply a theme to the document root element by setting or removing the
 * `data-theme` attribute.  CSS variables in `tailwind.css` respond to this
 * attribute to switch colour palettes.
 *
 * @param theme - The theme to apply.
 */
function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else if (theme === 'light') {
    root.setAttribute('data-theme', 'light');
  } else {
    root.removeAttribute('data-theme');
  }
}
